import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/**
 * Rutas que un rol concreto puede LEER, según `submenus_permisos`.
 *
 * `useAllowedMenus` responde siempre por el rol del usuario logueado, así que
 * un Super Administrador que elige "Ver como <Banco>" seguía viendo el menú
 * completo - incluida la vista Bancos, que administra los convenios de TODOS
 * los bancos. Parecía que el banco veía lo de los demás.
 *
 * Con esto el portal puede pintar el menú del rol impersonado (el Admin del
 * banco) en vez del propio. Es solo presentación: el acceso real lo siguen
 * gateando `PermissionRoute` y las policies de la BD.
 */
export function useVistasDelRol(rolId?: number | null) {
  return useQuery({
    queryKey: ["vistas-del-rol", rolId ?? "none"],
    enabled: rolId != null,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<Set<string>> => {
      const { data: permiso } = await (supabase as any)
        .from("permisos")
        .select("id")
        .eq("nombre", "leer")
        .maybeSingle();
      if (!permiso?.id) return new Set<string>();

      const { data: permisos } = await (supabase as any)
        .from("submenus_permisos")
        .select("submenu_id")
        .eq("rol_id", rolId)
        .eq("permiso_id", permiso.id)
        .eq("activo", true);
      const ids = (permisos ?? []).map((p: any) => p.submenu_id);
      if (ids.length === 0) return new Set<string>();

      // Un menú padre apagado apaga sus vistas aunque el submenú siga activo.
      const { data: submenus } = await (supabase as any)
        .from("submenus")
        .select("vista_front_end, menus!inner(activo)")
        .in("id", ids)
        .eq("activo", true)
        .eq("menus.activo", true);

      return new Set<string>(
        (submenus ?? [])
          .map((s: any) => s.vista_front_end as string)
          .filter(Boolean),
      );
    },
  });
}
